import React, { useEffect, useState, useCallback } from "react";
import { withPrefix } from "gatsby";
import { resolve } from "styled-jsx/css";
import { CardCarousel } from "../Card/CardCarousel";
import { Video } from "./Video";
import { VideoCard, getVideoUrl } from "./VideoCard";
import { AuthoringDate } from "../Article/AuthoringInfo";
import { ButtonLink, Link } from "../Link";
import { ThemeProvider, useTheme } from "../theme";
import { Modal } from "../Modal";
import BreadcrumbArrow from "./breadcrumb-arrow.svg";

const lineColor = "#e2e5e8";
const videoDetailsWidth = 50;

// TODO: Infer this somehow
const parentPagePath = "/noissuetv/";
const isLocationPathname = (path) =>
    window.location.pathname === withPrefix(path);
const pushState = (state, path) =>
    window.history?.pushState(state, "", withPrefix(path));
const replaceState = (state, path) =>
    window.history?.replaceState(state, "", withPrefix(path));
const goBack = () => window.history?.back();

function Breadcrumb({ className = "", title }) {
    const {
        altTextColor,
        secondaryTextColor,
        modularScale,
        contentWidth: maxWidth = videoDetailsWidth,
    } = useTheme();
    const contentWidth = Math.min(maxWidth, videoDetailsWidth);
    return (
        <nav className={`ng-breadcrumb ${className}`} aria-label="Breadcrumb">
            <ol className="ng-breadcrumb__list">
                <li className="ng-breadcrumb__item">
                    <Link
                        className="ng-breadcrumb__link ng-breadcrumb__link--ancestor"
                        to={parentPagePath}
                    >
                        noissuetv
                    </Link>
                </li>
                <li className="ng-breadcrumb__item">
                    <Link
                        className="ng-breadcrumb__link ng-breadcrumb__link--current"
                        to="./"
                        aria-current="page"
                    >
                        {title}
                    </Link>
                </li>
            </ol>
            <style jsx>{`
                .ng-breadcrumb {
                    max-width: ${contentWidth}em;
                    padding: 0 1.25em;
                    margin: 0 auto;
                }
                .ng-breadcrumb__list {
                    list-style-type: none;
                }
                .ng-breadcrumb__item {
                    display: inline-block;
                }
                .ng-breadcrumb__item + .ng-breadcrumb__item {
                    padding-left: 2em;
                    background-image: url(${BreadcrumbArrow});
                    background-repeat: no-repeat;
                    background-position: 0.8em 0.3em;
                    background-size: 0.4em;
                }
                .ng-breadcrumb :global(.ng-breadcrumb__link) {
                    font-size: ${modularScale(-1)}em;
                }
                .ng-breadcrumb :global(.ng-breadcrumb__link--ancestor) {
                    color: ${secondaryTextColor};
                }
                .ng-breadcrumb :global(.ng-breadcrumb__link--current) {
                    color: ${altTextColor};
                }
            `}</style>
        </nav>
    );
}

export function VideoDetails({
    className = "",
    video,
    related: { channel, videos: relatedVideos },
    onRelatedVideoOpen = null,
    showBreadcrumb = true,
}) {
    const { title, publishedDate, url, excerpt, postLink, productSlug } = video;
    const {
        altTextColor,
        modularScale,
        contentWidth: maxWidth = videoDetailsWidth,
    } = useTheme();
    const contentWidth = Math.min(maxWidth, videoDetailsWidth);
    const relatedCards = relatedVideos.map((relatedVideo) => {
        const { id, title, excerpt, publishedDate, slug, url } = relatedVideo;
        const onClick = (e) => {
            e.stopPropagation();
            return onRelatedVideoOpen(relatedVideo);
        };
        return {
            key: id,
            display: (
                <VideoCard
                    key={id}
                    title={title}
                    excerpt={excerpt}
                    slug={slug}
                    url={url}
                    publishedDate={publishedDate}
                    onClick={onRelatedVideoOpen && onClick}
                />
            ),
        };
    });

    const relatedVideosHeading = (
        <span className="related-videos__heading">
            <span>more from...</span> {channel.name}
            <style jsx>{`
                .related-videos__heading {
                    font-size: ${modularScale(-1)}em;
                }
                .related-videos__heading span {
                    color: #e9b092;
                }
            `}</style>
        </span>
    );

    return (
        <div className={`video-details ${className}`}>
            {showBreadcrumb && (
                <Breadcrumb
                    className="video-details__breadcrumb"
                    title={title}
                />
            )}
            <div className="video-details__video">
                <Video url={url} />
            </div>
            <div className="video-details__metadata">
                <h1 className="video-details__title">{title}</h1>
                <p className="video-details__excerpt">{excerpt}</p>
                {productSlug && (
                    <div className="video-details__post-link">
                        <ButtonLink to={productSlug} defaultLink={true}>Go to Product</ButtonLink>
                    </div>
                )}
                {!productSlug && postLink && (
                    <div className="video-details__post-link">
                        <ButtonLink to={postLink}>Read Brand Story</ButtonLink>
                    </div>
                )}
                <p className="video-details__published-date">
                    <AuthoringDate date={publishedDate} />
                </p>
            </div>
            {!!relatedCards.length && (
                <ThemeProvider theme={{ contentWidth }}>
                    <CardCarousel
                        className="video-details__related"
                        heading={relatedVideosHeading}
                        cards={relatedCards}
                    />
                </ThemeProvider>
            )}
            <style jsx>{`
                .video-details :global(.video-details__breadcrumb) {
                    padding-bottom: 1em;
                }
                .video-details :global(.video-details__video) {
                    margin: 0 auto;
                }
                @media (min-width: ${contentWidth + 2.5}em) {
                    .video-details :global(.video-details__video) {
                        max-width: ${contentWidth}em;
                        border-radius: 10px;
                        overflow: hidden;
                        // Stop video escaping border-radius on Safari
                        // https://stackoverflow.com/questions/49066011/overflow-hidden-with-border-radius-not-working-on-safari
                        position: relative;
                    }
                }
                .video-details__metadata {
                    max-width: 40em;
                    margin: 1.5em auto;
                    padding: 0 1.25em;
                }
                .video-details__excerpt {
                    padding-top: 1em;
                    border-top: 1px solid ${lineColor};
                    font-weight: 300;
                }
                .video-details__title {
                    font-size: ${modularScale(4)}em;
                    color: ${altTextColor};
                }
                .video-details__title,
                .video-details__excerpt,
                .video-details__post-link,
                .video-details__published-date {
                    margin-top: 1em;
                }
                .video-details__tags {
                    padding-top: 1em;
                    border-top: 1px solid ${lineColor};
                }
                .video-details :global(.video-details__related) {
                    margin-top: 2em;
                    padding-top: 2em;
                    border-top: 1px solid ${lineColor};
                }
            `}</style>
        </div>
    );
}

const { className: modalClassName, styles: modalStyles } = resolve`
    padding-top: 1em;
`;

export function VideoDetailsModal({
    video = null,
    related,
    onVideoOpen,
    onClose,
}) {
    const [videoProxy, setVideoProxy] = useState(video);
    // Introduce some hysteresis. Stop video details disappearing before modal
    // closing animation is complete. Else you see a flash with an X in the
    // centre of the screen
    useEffect(() => {
        setTimeout(() => setVideoProxy(video));
    }, [video]);

    const openVideo = useCallback(
        (vid) => {
            setTimeout(() => {
                const el = document.getElementById("videoDetailsModal")
                    ?.parentElement;
                if (el) {
                    el.scrollTop = 0;
                }
            });
            onVideoOpen(vid);
        },
        [onVideoOpen]
    );

    useEffect(() => {
        if (video === null) {
            return;
        }

        const { slug } = video;
        const url = getVideoUrl(slug);
        if (!isLocationPathname(url) && window.history.state?.slug !== slug) {
            pushState(video, parentPagePath);
            pushState(video, url);
        }
        // TODO: scroll modal to top
    }, [video]);

    useEffect(() => {
        const onPopState = (e) => {
            if (!isLocationPathname(parentPagePath)) {
                return;
            }

            const state = e?.state;
            if (state?.slug) {
                // Going back
                onClose();
                // "mark" the state to differentiate going forward from going
                // back in browser history
                replaceState({ mark: true, video }, parentPagePath);
                goBack();
            } else if (state?.mark && state?.video) {
                // Going forward
                replaceState(state.video, parentPagePath);
                pushState(state.video, getVideoUrl(state.video.slug));
                openVideo(state.video);
            }
        };
        // TODO: Use addEventListener
        window.onpopstate = (e) => setTimeout(() => onPopState(e));
        return () => {
            window.onpopstate = null;
        };
    }, [video, openVideo, onClose]);

    const onModalClose = () => {
        goBack();
    };

    const onRelatedOpen = (vid) => {
        const { slug } = vid;
        replaceState(vid, getVideoUrl(slug));
        return openVideo(vid);
    };

    return (
        <ThemeProvider theme={{ contentWidth: videoDetailsWidth }}>
            <Modal
                modalId="videoDetailsModal"
                open={!!video}
                onClose={onModalClose}
            >
                {videoProxy && (
                    <VideoDetails
                        className={modalClassName}
                        video={videoProxy}
                        related={related}
                        onRelatedVideoOpen={onRelatedOpen}
                        showBreadcrumb={false}
                    />
                )}
                {modalStyles}
            </Modal>
        </ThemeProvider>
    );
}
